import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Collapse,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Typography,
} from '@mui/material';

export interface AccordionProps {
  title: string;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
  children: React.ReactNode;
  disabled?: boolean;
  collapsed?: boolean;
}

/**
 * The Collapse wrapper hides the accordion from MUI's sibling selectors, so
 * the divider and square corners of a middle accordion are restored here.
 */
const collapsibleAccordionSx = {
  '&:first-of-type, &:last-of-type': { borderRadius: 0 },
  '&:first-of-type::before': { display: 'block' },
};

const collapseSx = {
  '.Mui-expanded + & .MuiAccordion-root::before': { display: 'none' },
  '&:has(.Mui-expanded) + .MuiAccordion-root::before': { display: 'none' },
};

const Accordion: React.FC<AccordionProps> = ({
  title,
  expanded,
  onChange,
  children,
  disabled = false,
  collapsed,
}) => {
  const expandedEffective = expanded && !disabled && !collapsed;
  const collapsible = collapsed !== undefined;

  const accordion = (
    <MuiAccordion
      disableGutters
      expanded={expandedEffective}
      onChange={(_, nextExpanded) => {
        if (disabled) return;
        onChange(nextExpanded);
      }}
      sx={collapsible ? collapsibleAccordionSx : undefined}
    >
      <MuiAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          variant="caption"
          color={expandedEffective ? 'primary' : 'text.secondary'}
          sx={{ textDecoration: disabled ? 'line-through' : 'none' }}
        >
          {title}
        </Typography>
      </MuiAccordionSummary>
      <MuiAccordionDetails
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {children}
      </MuiAccordionDetails>
    </MuiAccordion>
  );

  if (!collapsible) {
    return accordion;
  }
  return (
    <Collapse in={!collapsed} unmountOnExit sx={collapseSx}>
      {accordion}
    </Collapse>
  );
};

export default Accordion;
